import { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, ChevronRight } from 'lucide-react';
import type { GuideConfig, GuideArticle } from '@edison/help';

interface GuidesColumnProps {
  config: GuideConfig;
  citedGuideId: string | null;
  selectedArticleId: string | null;
  onArticleSelect: (article: GuideArticle) => void;
  onClearCitation: () => void;
}

export function GuidesColumn({ config, citedGuideId, selectedArticleId, onArticleSelect, onClearCitation }: GuidesColumnProps) {
  const [query, setQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const articleRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const popularArticles = config.categories.flatMap((c) => c.articles).filter((a) => a.popular);

  // When citedGuideId changes, expand its parent category and scroll to it
  useEffect(() => {
    if (!citedGuideId) return;
    const parentCategory = config.categories.find((c) =>
      c.articles.some((a) => a.id === citedGuideId)
    );
    if (parentCategory) {
      setExpandedCategories((prev) => new Set([...prev, parentCategory.id]));
      setTimeout(() => {
        articleRefs.current[citedGuideId]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [citedGuideId, config]);

  const filteredCategories = query.trim()
    ? config.categories
        .map((cat) => ({
          ...cat,
          articles: cat.articles.filter(
            (a) =>
              a.title.toLowerCase().includes(query.toLowerCase()) ||
              a.content.toLowerCase().includes(query.toLowerCase())
          ),
        }))
        .filter((cat) => cat.articles.length > 0)
    : config.categories;

  function toggleCategory(id: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSearchChange(value: string) {
    setQuery(value);
    if (citedGuideId) onClearCitation();
  }

  function ArticleRow({ article }: { article: GuideArticle }) {
    const isCited = article.id === citedGuideId;
    const isSelected = article.id === selectedArticleId;

    return (
      <div
        ref={(el) => { articleRefs.current[article.id] = el; }}
        role="button"
        tabIndex={0}
        onClick={() => onArticleSelect(article)}
        onKeyDown={(e) => { if (e.key === 'Enter') onArticleSelect(article); }}
        className={`px-3 py-2 rounded-md text-sm cursor-pointer transition-colors ${
          isCited
            ? 'border-l-2 border-blue-500 bg-blue-500/8 text-blue-300 pl-2'
            : isSelected
            ? 'bg-white/12 text-primary-foreground border-l-2 border-white/40 pl-2'
            : 'text-primary-foreground/60 hover:text-primary-foreground hover:bg-white/5'
        }`}
      >
        <span className="truncate block">{article.title}</span>
        {isCited && (
          <span className="text-xs text-blue-400 mt-0.5 block">Cited by AI</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Search */}
      <div className="p-3 border-b border-white/8 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search guides..."
            className="w-full bg-white/5 rounded-md pl-8 pr-3 py-1.5 text-sm text-primary-foreground placeholder:text-white/30 outline-none focus:ring-1 focus:ring-white/20"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Popular section — only shown when not searching */}
        {!query && popularArticles.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/30 px-1 mb-2">
              Popular
            </p>
            <div className="space-y-0.5">
              {popularArticles.map((a) => (
                <ArticleRow key={a.id} article={a} />
              ))}
            </div>
          </div>
        )}

        {/* Categories */}
        <div className="space-y-1">
          {!query && (
            <p className="text-xs font-semibold uppercase tracking-widest text-white/30 px-1 mb-2">
              Categories
            </p>
          )}
          {filteredCategories.map((cat) => {
            const isExpanded = expandedCategories.has(cat.id) || !!query;
            return (
              <div key={cat.id}>
                {!query && (
                  <button
                    onClick={() => toggleCategory(cat.id)}
                    className="flex items-center justify-between w-full px-2 py-1.5 text-sm text-primary-foreground/70 hover:text-primary-foreground rounded-md hover:bg-white/5 transition-colors"
                  >
                    <span>{cat.label}</span>
                    {isExpanded
                      ? <ChevronDown className="h-3.5 w-3.5" />
                      : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                )}
                {(isExpanded || !!query) && (
                  <div className="space-y-0.5 ml-1">
                    {cat.articles.map((a) => (
                      <ArticleRow key={a.id} article={a} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
